import React, { useEffect, useState } from "react";
import { Brain, Shield, ShieldAlert, ShieldCheck, CheckCircle2, XCircle, Sparkles } from "lucide-react";

/**
 * 9-stage adaptive-learning UI:
 *   Patterns list (stages 6+8), Challenger stats (stages 2+7), Recent learning events (stages 4+5).
 * Props: { fetcher, accent } — accent hex color, fetcher returns {events, patterns, challenger}
 */
export default function LearningPanel({ fetcher, accent = "#FFCC00" }) {
  const [data, setData] = useState({ events: [], patterns: [], challenger: null });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const d = await fetcher();
        if (mounted) setData(d || { events: [], patterns: [], challenger: null });
      } catch { /* */ }
      finally { if (mounted) setLoading(false); }
    };
    load();
    const iv = setInterval(load, 60000);
    return () => { mounted = false; clearInterval(iv); };
  }, [fetcher]);

  const { events = [], patterns = [], challenger } = data;
  const prefer = patterns.filter(p => p.promoted === "PREFER");
  const avoid = patterns.filter(p => p.promoted === "AVOID");
  const observed = patterns.filter(p => !p.promoted);

  return (
    <section className="rounded-2xl border border-zinc-900 bg-zinc-950/40 p-4 md:p-5 space-y-4" data-testid="learning-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Brain className="w-4 h-4" style={{ color: accent }} />
          <div>
            <div className="text-sm font-semibold text-white">Adaptive Learning · 9-Stage Pipeline</div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Signal → Challenger → Result → Error Analysis → Pattern → Validation → Model Improvement</div>
          </div>
        </div>
        <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{events.length} events</span>
      </div>

      {/* Challenger stats */}
      {challenger && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
          <StatChip
            testid="challenger-status"
            icon={challenger.trusted ? <ShieldCheck className="w-3.5 h-3.5" /> : <ShieldAlert className="w-3.5 h-3.5" />}
            label="Challenger"
            value={challenger.trusted ? "Trusted" : "Learning"}
            tone={challenger.trusted ? "up" : "flat"}
          />
          <StatChip testid="challenger-accuracy" label="Accuracy" value={`${(challenger.accuracy * 100).toFixed(0)}%`} />
          <StatChip testid="challenger-samples" label="Verdicts" value={`${challenger.correct}/${challenger.total}`} />
          <StatChip testid="promoted-count" label="Promoted" value={`${prefer.length}✓ · ${avoid.length}✗`} />
        </div>
      )}

      {/* Patterns */}
      {patterns.length === 0 ? (
        <div className="text-xs text-zinc-500 rounded-lg border border-zinc-900 bg-black/30 px-3 py-4 text-center">
          {loading ? "Loading learning state…" : "Bot needs at least 5 closed trades before patterns emerge. Keep the engine running."}
        </div>
      ) : (
        <div className="space-y-3">
          {prefer.length > 0 && (
            <PatternGroup title="Prefer" tone="up" icon={<CheckCircle2 className="w-3.5 h-3.5" />} items={prefer} />
          )}
          {avoid.length > 0 && (
            <PatternGroup title="Avoid" tone="down" icon={<XCircle className="w-3.5 h-3.5" />} items={avoid} />
          )}
          {observed.length > 0 && (
            <PatternGroup title="Observed" tone="flat" icon={<Sparkles className="w-3.5 h-3.5" />} items={observed.slice(0, 5)} />
          )}
        </div>
      )}

      {/* Recent Learning Events */}
      {events.length > 0 && (
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">Recent Learning Events</div>
          <div className="rounded-lg border border-zinc-900 bg-black/20 divide-y divide-zinc-900 max-h-56 overflow-y-auto">
            {events.slice(0, 12).map((e, i) => (
              <div key={i} data-testid={`learning-event-${i}`} className="px-3 py-2 text-xs">
                <div className="flex items-center gap-2">
                  <span className={`font-mono text-[10px] px-1.5 py-0.5 rounded ${
                    e.result === "WIN" ? "bg-emerald-500/10 text-emerald-400"
                    : e.result === "LOSS" ? "bg-red-500/10 text-red-400"
                    : "bg-zinc-800 text-zinc-400"
                  }`}>{e.result}</span>
                  <span className="text-white">{e.ticker_or_underlying} {e.direction}{e.strike ? ` ${e.strike}` : ""}</span>
                  <span className="text-zinc-500 ml-auto">₹{Number(e.pnl || 0).toFixed(0)}</span>
                </div>
                {e.root_cause && (
                  <div className="text-[11px] text-zinc-400 mt-1">{e.root_cause}</div>
                )}
                {e.tags?.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {e.tags.slice(0, 4).map((t, j) => (
                      <span key={j} className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-900 text-zinc-400 font-mono">{t}</span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function StatChip({ testid, icon, label, value, tone }) {
  const cls = tone === "up" ? "text-emerald-400" : tone === "down" ? "text-red-400" : "text-zinc-300";
  return (
    <div data-testid={testid} className="rounded-lg border border-zinc-900 bg-black/30 px-3 py-2">
      <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
        {icon} {label}
      </div>
      <div className={`font-mono text-sm mt-0.5 ${cls}`}>{value}</div>
    </div>
  );
}

function PatternGroup({ title, tone, icon, items }) {
  const border = tone === "up" ? "border-emerald-500/20 bg-emerald-500/5"
    : tone === "down" ? "border-red-500/20 bg-red-500/5"
    : "border-zinc-800 bg-black/20";
  const textCls = tone === "up" ? "text-emerald-400" : tone === "down" ? "text-red-400" : "text-zinc-400";
  return (
    <div className={`rounded-lg border ${border} p-3`}>
      <div className={`text-[10px] font-mono uppercase tracking-widest flex items-center gap-1.5 ${textCls}`}>
        {icon} {title} ({items.length})
      </div>
      <div className="mt-2 space-y-1.5">
        {items.map((p, i) => (
          <div key={i} className="flex items-start justify-between gap-3 text-xs">
            <div className="text-zinc-200">{p.pattern}</div>
            <div className="font-mono text-[11px] text-zinc-400 whitespace-nowrap">
              WR {(p.win_rate * 100).toFixed(0)}% · {p.win_count}W/{p.loss_count}L · n={p.samples}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
